import cx from 'classnames'
import Styles from './DueDatePicker.module.scss'
import dayjs from 'dayjs'
import { DueDatePickerProps, DueDatePickerSize, TriggerProps } from './Types'
import { displayDateFrom, isToday, isTomorrow } from 'utils/dates'
import { ReactComponent as CalendarIcon } from 'assets/icons/calendar16.svg'
import { IconButton } from 'components/IconButton/IconButton'
import { forwardRef, ReactNode, useCallback, useMemo, useState } from 'react'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { Calendar } from 'components/Calendar/Calendar'
import { Pressable } from '@react-aria/interactions'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { Button } from 'blaze-ui/Button'
import { ButtonSize, ButtonVariant } from 'blaze-ui/Button/Types'

const sizeMapping: { [key in DueDatePickerSize]: ButtonSize } = {
  xs: 'sm',
  sm: 'sm',
  md: 'md',
  lg: 'lg',
}

const Trigger = forwardRef<HTMLButtonElement, TriggerProps>(
  (
    {
      value,
      size = 'md',
      variant = 'grey',
      displayText,
      shape = 'rounded',
      className,
      frontIcon,
      isOpen,
      hideChevron,
      showEmptyLabel = false,
      preventFocusOnPress = false,
      ...props
    },
    fRef,
  ) => {
    const overdue = value && dayjs().isAfter(dayjs(value), 'day')
    const today = value && isToday(value)
    const tomorrow = value && isTomorrow(value)
    const chevronDirection = isOpen ? 'up' : 'down'
    const classNames = cx(Styles.button, className, Styles[size], Styles[shape], Styles[variant], {
      [Styles.overdue]: overdue,
      [Styles.today]: today,
      [Styles.tomorrow]: tomorrow,
      [Styles.blank]: !value,
    })

    const label = useMemo(() => {
      if (displayText && value) {
        return displayText(value)
      }
      return value ? `Due ${displayDateFrom(value)}` : 'Due date'
    }, [displayText, value])

    if (['xs', 'sm'].includes(size) && !value && !showEmptyLabel) {
      return (
        <IconButton
          preventFocusOnPress={preventFocusOnPress}
          size="sm"
          icon={CalendarIcon}
          {...props}
          data-testid="date-picker-button"
        />
      )
    }

    if (shape === 'squared') {
      return (
        <Button
          {...props}
          variant={variant as ButtonVariant}
          size={sizeMapping[size]}
          data-testid="date-picker-button"
          preventFocusOnPress={preventFocusOnPress}
          frontIcon={frontIcon}
          withChevron={hideChevron ? undefined : chevronDirection}
        >
          {label}
        </Button>
      )
    }

    const buttonProps = { className: classNames, 'data-testid': 'date-picker-button', ...props }

    let content: ReactNode = (
      <>
        <CalendarIcon />
        {label}
      </>
    )

    if (size === 'xs') {
      content = value ? label : <CalendarIcon />
    }

    if (size === 'sm') {
      content = label
    }

    return (
      <Pressable>
        <button {...buttonProps} ref={fRef}>
          {content}
        </button>
      </Pressable>
    )
  },
)

const DueDatePicker = ({
  value,
  onChange,
  size = 'md',
  displayText,
  shape = 'rounded',
  readOnly = false,
  hideChevron = false,
  showEmptyLabel = false,
  variant = 'tertiary',
  onOpenChange,
  frontIcon,
  forceOpen = false,
  preventFocusOnPress = false,
  showClearDate = true,
  ...rest
}: DueDatePickerProps) => {
  const [isOpen, setIsOpen] = useState(false)

  const handleOpenChange = useCallback(
    (open: boolean) => {
      onOpenChange?.(open)
      setIsOpen(open)
    },
    [onOpenChange],
  )

  if (readOnly) {
    return (
      <Trigger
        value={value}
        size={size}
        displayText={displayText}
        variant={variant}
        shape={shape}
        frontIcon={frontIcon}
        isOpen={isOpen}
        hideChevron={hideChevron}
        showEmptyLabel={showEmptyLabel}
        preventFocusOnPress={preventFocusOnPress}
      />
    )
  }

  return (
    <DropdownTrigger onOpenChange={handleOpenChange} forceOpen={forceOpen}>
      <Tooltip label={value ? 'Change due date' : 'Set due date'} mouseEnterDelay={1000}>
        <span>
          <Trigger
            value={value}
            size={size}
            displayText={displayText}
            variant={variant}
            shape={shape}
            frontIcon={frontIcon}
            isOpen={isOpen}
            hideChevron={hideChevron}
            showEmptyLabel={showEmptyLabel}
            preventFocusOnPress={preventFocusOnPress}
          />
        </span>
      </Tooltip>
      {(close) => (
        <Calendar
          showClearDate={showClearDate}
          data-testid="calendar"
          value={value || undefined}
          onChange={(date) => {
            onChange?.(date || null)
            close()
          }}
          {...rest}
        />
      )}
    </DropdownTrigger>
  )
}

export { DueDatePicker }
